import { useMemo } from "react";

import { ArrowRightOutlined } from "@ant-design/icons";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { FilterBarRange, Tag, TST_DEFAULT_PAGE_SIZE } from "@impero-com/ui";
import { usePresetTableSearchParams } from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    CellContext,
    Column,
    createColumnHelper,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { ArrayOfAssessmentDto as GetRiskInsightsDto } from "api/generated/model";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";

import { createFilterFnWithMode } from "../../../../utils/table/createFilterFnWithMode";
import { tstArrIncludesSomeFilterFn } from "../../../../utils/table/tstArrIncludesSomeFilterFn";
import { tagsTSTFilterFn } from "../../../tags/tagsTSTUtils";

import { insightsStatusCell } from "./components/InsightsStatusCell.css";
import { percentageChangeRoot } from "./components/PercentageChange.css";
import { RiskTag } from "./components/RiskChange";
import { riskChangeRoot } from "./components/RiskChange.css";
import { INHERENT_MODE, MapMode } from "./components/RiskHeatmapTypes";
import { parseFocusPairKey } from "./mapInsightsHeatmap";
import {
    Delta,
    MAP_INSIGHTS_COLUMN_IDS,
    mapInsightsColumnFiltersCodecMap,
    MapInsightsColumnFilters,
    RiskInsightsTableRow,
    ScaleValue,
    Values,
} from "./mapInsightsModel";
import {
    extractDeltaValue,
    extractRiskNodes,
    extractValues,
    foldTree,
    mapDeltaValue,
    normalizeNullableDelta,
} from "./mapInsightsUtils";

const columnHelper = createColumnHelper<RiskInsightsTableRow>();

function createColumns(
    linkedResources: GetRiskInsightsDto["linkedResources"],
    mapMode: MapMode,
    t: TFunction
) {
    const columnNames = {
        inherentImpact: extractDeltaValue(
            linkedResources.blessedColumns.inherentImpact.name
        ),
        inherentLikelihood: extractDeltaValue(
            linkedResources.blessedColumns.inherentLikelihood.name
        ),
        residualImpact: extractDeltaValue(
            linkedResources.blessedColumns.residualImpact.name
        ),
        residualLikelihood: extractDeltaValue(
            linkedResources.blessedColumns.residualLikelihood.name
        ),
    };
    const optionsById = Object.fromEntries([
        ...extractValues(linkedResources.blessedColumns.inherentImpact.options)
            .flat()
            .map((option) => [option.id, option.label] as const),
        ...extractValues(
            linkedResources.blessedColumns.inherentLikelihood.options
        )
            .flat()
            .map((option) => [option.id, option.label] as const),
        ...extractValues(linkedResources.blessedColumns.residualImpact.options)
            .flat()
            .map((option) => [option.id, option.label] as const),
        ...extractValues(
            linkedResources.blessedColumns.residualLikelihood.options
        )
            .flat()
            .map((option) => [option.id, option.label] as const),
    ]);

    // Processes and risks ids does not overlap, therefore One Map to rule them all
    const namesById = foldTree<Record<string, string>>(
        linkedResources.rootNode.nodes,
        {},
        (map, node) => {
            map[node.id] = extractDeltaValue(node.name);
            return map;
        }
    );
    const entitiesById = Object.fromEntries(
        linkedResources.entities.map((entity) => [
            entity.id,
            extractDeltaValue(entity.name),
        ])
    );
    const baseColumns = [
        columnHelper.accessor((row) => row.path, {
            id: MAP_INSIGHTS_COLUMN_IDS.PATH,
            header: () => t("RiskDirectory_Insights_HeaderProcess"),
            cell: (info) => info.getValue().join(" / "),
            enableSorting: true,
            enableColumnFilter: true,
            // Works functionally like a tag, matching when there is at least a match.
            // We could give this function a better name tho.
            filterFn: createFilterFnWithMode<RiskInsightsTableRow, string[]>(
                (row, ids) =>
                    tagsTSTFilterFn(
                        row.original.path,
                        ids.map((id) => namesById[id])
                    )
            ),
        }),
        columnHelper.accessor((row) => row.name, {
            id: MAP_INSIGHTS_COLUMN_IDS.RISK,
            header: () => t("RiskDirectory_Insights_HeaderRisk"),
            cell: (info) => <RiskNameCell info={info} t={t} />,
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<RiskInsightsTableRow, string[]>(
                (row, ids) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        MAP_INSIGHTS_COLUMN_IDS.RISK,
                        ids.map((id) => namesById[id])
                    )
            ),
        }),
        columnHelper.accessor((row) => row.entity, {
            id: MAP_INSIGHTS_COLUMN_IDS.ENTITY,
            header: () => t("RiskDirectory_Insights_HeaderEntity"),
            cell: (info) => info.getValue(),
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<RiskInsightsTableRow, string[]>(
                (row, ids) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        MAP_INSIGHTS_COLUMN_IDS.ENTITY,
                        ids.map((id) => entitiesById[id])
                    )
            ),
        }),
    ];

    // The inherent/residual shares the same ID for filtering purposes.
    // They are never shown at the same time.
    type ScaleField =
        | "inherentImpact"
        | "inherentLikelihood"
        | "residualImpact"
        | "residualLikelihood";
    type ScaleColumnId =
        | typeof MAP_INSIGHTS_COLUMN_IDS.IMPACT
        | typeof MAP_INSIGHTS_COLUMN_IDS.LIKELIHOOD;

    // Inherent and residual share the same column ids and are never shown at the
    // same time; mapMode selects which pair of fields to read.
    const scaleColumn = (field: ScaleField, columnId: ScaleColumnId) =>
        columnHelper.accessor((row) => row[field], {
            id: columnId,
            header: () => columnNames[field],
            cell: (info) => <RiskChange delta={info.getValue()} />,
            enableSorting: false,
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<RiskInsightsTableRow, string[]>(
                (row, ids) =>
                    tagsTSTFilterFn(
                        extractValues(row.original[field]).map(
                            (value) => value?.label ?? ""
                        ),
                        ids.map((id) => optionsById[id])
                    )
            ),
        });

    const scaleColumns =
        mapMode === INHERENT_MODE
            ? [
                  scaleColumn("inherentImpact", MAP_INSIGHTS_COLUMN_IDS.IMPACT),
                  scaleColumn(
                      "inherentLikelihood",
                      MAP_INSIGHTS_COLUMN_IDS.LIKELIHOOD
                  ),
              ]
            : [
                  scaleColumn("residualImpact", MAP_INSIGHTS_COLUMN_IDS.IMPACT),
                  scaleColumn(
                      "residualLikelihood",
                      MAP_INSIGHTS_COLUMN_IDS.LIKELIHOOD
                  ),
              ];

    const coverageColumn = columnHelper.accessor((row) => row.coverage, {
        id: MAP_INSIGHTS_COLUMN_IDS.COVERAGE,
        header: () => t("RiskDirectory_Insights_HeaderCoverage"),
        cell: (info) => <PercentageChange delta={info.getValue()} />,
        enableSorting: false,
        filterFn: createFilterFnWithMode<
            RiskInsightsTableRow,
            [number | null, number | null]
        >((row, filterValue) => {
            const value: Delta<number | null> = row.getValue(
                MAP_INSIGHTS_COLUMN_IDS.COVERAGE
            );
            const check = (value: number | null) => {
                if (typeof value !== "number") {
                    return false;
                }
                const [min, max] = filterValue;
                const minCheck = min === null || value >= min;
                const maxCheck = max === null || value <= max;
                return minCheck && maxCheck;
            };
            return extractValues(value).some(check);
        }),
    });

    const focusColumn = columnHelper.display({
        id: MAP_INSIGHTS_COLUMN_IDS.FOCUS,
        enableHiding: true,
        enableColumnFilter: true,
        filterFn: createFilterFnWithMode<RiskInsightsTableRow, string[]>(
            (row, pairs) => {
                const impact =
                    mapMode === INHERENT_MODE
                        ? row.original.inherentImpact
                        : row.original.residualImpact;
                const likelihood =
                    mapMode === INHERENT_MODE
                        ? row.original.inherentLikelihood
                        : row.original.residualLikelihood;
                return pairs.some((pair) => {
                    const { x: xIdx, y: yIdx } = parseFocusPairKey(pair);
                    return (
                        extractValues(likelihood).some(
                            (v) => v?.index === xIdx
                        ) &&
                        extractValues(impact).some((v) => v?.index === yIdx)
                    );
                });
            }
        ),
    });

    return [...baseColumns, ...scaleColumns, coverageColumn, focusColumn];
}

function RiskNameCell({
    info,
    t,
}: {
    info: CellContext<RiskInsightsTableRow, string>;
    t: TFunction;
}) {
    const onlyAt = info.row.original.onlyAt;
    return (
        <span className={insightsStatusCell}>
            {onlyAt === "End" && (
                <Tag variant="success">{t("RiskDirectory_Insights_New")}</Tag>
            )}
            {onlyAt === "Start" && (
                <Tag variant="error">{t("RiskDirectory_Insights_Removed")}</Tag>
            )}
            {info.getValue()}
        </span>
    );
}

export function RiskChange({ delta }: { delta: Delta<ScaleValue | null> }) {
    const { t } = useTranslation();
    switch (delta.type) {
        case "Unchanged":
            return (
                <RiskTag
                    label={delta.value?.label ?? "-"}
                    index={delta.value?.index}
                />
            );
        case "Changed":
            return (
                <div className={riskChangeRoot}>
                    <RiskTag
                        label={
                            delta.start?.label ??
                            t("RiskDirectory_RiskChange_NoValue")
                        }
                        index={delta.start?.index}
                    />
                    <ArrowRightOutlined />
                    <RiskTag
                        label={
                            delta.end?.label ??
                            t("RiskDirectory_RiskChange_NoValue")
                        }
                        index={delta.end?.index}
                    />
                </div>
            );
    }
}

export function PercentageChange({ delta }: { delta: Delta<number | null> }) {
    switch (delta.type) {
        case "Unchanged":
            return delta.value !== null ? (
                <span>{delta.value}%</span>
            ) : (
                <span>-</span>
            );
        case "Changed":
            return (
                <div className={percentageChangeRoot}>
                    {delta.start !== null ? `${delta.start}%` : "-"}{" "}
                    <ArrowRightOutlined />{" "}
                    {delta.end !== null ? `${delta.end}%` : "-"}
                </div>
            );
        default:
            assertNever(delta);
    }
}

function extractRows(data: GetRiskInsightsDto): RiskInsightsTableRow[] {
    const entityMap = Object.fromEntries(
        data.linkedResources.entities.map((entity) => [entity.id, entity])
    );
    const riskMap = Object.fromEntries(
        extractRiskNodes(data.linkedResources.rootNode).map((node) => [
            node.id,
            node,
        ])
    );
    const inherentImpactMap = Object.fromEntries(
        extractDeltaValue(
            data.linkedResources.blessedColumns.inherentImpact.options
        ).map((option, index) => [option.id, { label: option.label, index }])
    );
    const inherentLikelihoodMap = Object.fromEntries(
        extractDeltaValue(
            data.linkedResources.blessedColumns.inherentLikelihood.options
        ).map((option, index) => [option.id, { label: option.label, index }])
    );
    const residualImpactMap = Object.fromEntries(
        extractDeltaValue(
            data.linkedResources.blessedColumns.residualImpact.options
        ).map((option, index) => [option.id, { label: option.label, index }])
    );
    const residualLikelihoodMap = Object.fromEntries(
        extractDeltaValue(
            data.linkedResources.blessedColumns.residualLikelihood.options
        ).map((option, index) => [option.id, { label: option.label, index }])
    );

    return data.content.resource.map((assessment) => {
        return {
            id: `${assessment.riskId}-${assessment.entityId}`,
            entity: extractDeltaValue(entityMap[assessment.entityId].name),
            name: extractDeltaValue(riskMap[assessment.riskId].name),
            onlyAt: assessment.onlyAt ?? null,
            path: riskMap[assessment.riskId].path,
            inherentImpact: mapDeltaValue(
                normalizeNullableDelta(assessment.inherentImpact),
                (value) => (value !== null ? inherentImpactMap[value] : null)
            ),
            inherentLikelihood: mapDeltaValue(
                normalizeNullableDelta(assessment.inherentLikelihood),
                (value) =>
                    value !== null ? inherentLikelihoodMap[value] : null
            ),
            residualImpact: mapDeltaValue(
                normalizeNullableDelta(assessment.residualImpact),
                (value) => (value !== null ? residualImpactMap[value] : null)
            ),
            residualLikelihood: mapDeltaValue(
                normalizeNullableDelta(assessment.residualLikelihood),
                (value) =>
                    value !== null ? residualLikelihoodMap[value] : null
            ),
            coverage: normalizeNullableDelta(assessment.coverage),
        };
    });
}

export function useMapInsightsTable(
    data: GetRiskInsightsDto,
    mapMode: MapMode,
    id: number,
    entityId?: number
) {
    const { t } = useTranslation();

    // extractRows ignores mapMode, but we depend on it anyway to force a new rows
    // reference on mode change so react-table rebuilds its cached row models (the
    // columns swap inherent<->residual under the same IMPACT/LIKELIHOOD ids).
    const rows = useMemo(() => extractRows(data), [data, mapMode]); // eslint-disable-line react-hooks/exhaustive-deps
    const notificationLabel = t("Table_RemovedFiltersWarning");
    const stateAndOnChanges = usePresetTableSearchParams(
        `riskInsights-${JSON.stringify([id, entityId])}`,
        { sorting: [] },
        mapInsightsColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );

    const columns = createColumns(data.linkedResources, mapMode, t);
    const table = useReactTable({
        ...stateAndOnChanges,
        state: {
            ...stateAndOnChanges.state,
            columnVisibility: {
                ...stateAndOnChanges.state.columnVisibility,
                // Always hide the focus column, it's just meant for heatmap filtering
                [MAP_INSIGHTS_COLUMN_IDS.FOCUS]: false,
            },
        },
        data: rows,
        columns,
        initialState: {
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        getCoreRowModel: getCoreRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const filterComponent = (column: Column<RiskInsightsTableRow>) =>
        getFilterComponent(t, column, data.linkedResources);

    return {
        table,
        getFilterComponent: filterComponent,
    };
}

const getFilterComponent = (
    t: TFunction,
    column: Column<RiskInsightsTableRow>,
    linkedResources: GetRiskInsightsDto["linkedResources"]
) => {
    const getFilterHelpers =
        createFilterHelpersGetter<MapInsightsColumnFilters>(column);
    const columnId = column.id as Values<typeof MAP_INSIGHTS_COLUMN_IDS>;

    switch (columnId) {
        case MAP_INSIGHTS_COLUMN_IDS.PATH: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const processes = foldTree(
                linkedResources.rootNode.nodes,
                // process name => id
                new Map<string, string>(),
                (processes, node) => {
                    const name = extractDeltaValue(node.name);
                    if (node.type === "Process" && !processes.has(name)) {
                        processes.set(name, node.id);
                    }
                    return processes;
                }
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={[...processes].map(([label, value]) => ({
                        label,
                        value,
                    }))}
                    placeholder={t("RiskInsights_Filter_Process_Placeholder")}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case MAP_INSIGHTS_COLUMN_IDS.RISK: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const visibleRisks = [
                ...column.getFacetedUniqueValues().keys(),
            ] as string[];
            const risks = Object.fromEntries(
                foldTree(
                    linkedResources.rootNode.nodes,
                    // risk name => id
                    new Map<string, string>(),
                    (risks, node) => {
                        const name = extractDeltaValue(node.name);
                        if (node.type === "Risk" && !risks.has(name)) {
                            risks.set(name, node.id);
                        }
                        return risks;
                    }
                )
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={visibleRisks.map((risk) => ({
                        label: risk,
                        value: risks[risk],
                    }))}
                    placeholder={t("RiskInsights_Filter_Risk_Placeholder")}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case MAP_INSIGHTS_COLUMN_IDS.ENTITY: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const visibleEntities = [
                ...column.getFacetedUniqueValues().keys(),
            ] as string[];
            const entities = Object.fromEntries(
                linkedResources.entities.map((entity) => [
                    extractDeltaValue(entity.name),
                    entity.id,
                ])
            );

            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={visibleEntities.map((entity) => ({
                        label: entity,
                        value: entities[entity],
                    }))}
                    placeholder={t("RiskInsights_Filter_Entity_Placeholder")}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case MAP_INSIGHTS_COLUMN_IDS.IMPACT: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            // Inherent and residuals shares the same scale labels, so using inherent unconditionally is correct.
            // label => id
            const options = new Map<string, string>();
            for (const option of extractValues(
                linkedResources.blessedColumns.inherentImpact.options
            )
                // Prefer the end values first, adding the missing start values at the end
                .reverse()
                .flat()) {
                if (!options.has(option.label)) {
                    options.set(option.label, option.id);
                }
            }

            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={[...options.entries()].map(([label, value]) => ({
                        label,
                        value,
                    }))}
                    placeholder={t("RiskInsights_Filter_Impact_Placeholder")}
                    setFilterValue={setFilterValue}
                    presorted
                />
            );
        }
        case MAP_INSIGHTS_COLUMN_IDS.LIKELIHOOD: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            // Inherent and residuals shares the same scale labels, so using inherent unconditionally is correct.
            // label => id
            const options = new Map<string, string>();
            for (const option of extractValues(
                linkedResources.blessedColumns.inherentLikelihood.options
            )
                // Prefer the end values first, adding the missing start values at the end
                .reverse()
                .flat()) {
                if (!options.has(option.label)) {
                    options.set(option.label, option.id);
                }
            }

            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={[...options.entries()].map(([label, value]) => ({
                        label,
                        value,
                    }))}
                    placeholder={t(
                        "RiskInsights_Filter_Likelihood_Placeholder"
                    )}
                    setFilterValue={setFilterValue}
                    presorted
                />
            );
        }
        case MAP_INSIGHTS_COLUMN_IDS.COVERAGE: {
            const [min, max] = column.getFacetedRowModel().flatRows.reduce(
                ([min, max], row) => {
                    const values = extractValues(row.original.coverage).filter(
                        (value) => value !== null
                    );
                    return [
                        values.length ? Math.min(min ?? 100, ...values) : min,
                        values.length ? Math.max(max ?? 0, ...values) : max,
                    ];
                },
                [null, null] as [number | null, number | null]
            );
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <FilterBarRange
                    min={min ?? 0}
                    max={max ?? 100}
                    maxPlaceholderLabel={t("FilterNumRange_MaxPlaceholder")}
                    minPlaceholderLabel={t("FilterNumRange_MinPlaceholder")}
                    value={value as [number, number]}
                    onChange={(updater) => {
                        const prev = value ?? [null, null];
                        setFilterValue(updater(prev as [number, number]));
                    }}
                />
            );
        }
        case MAP_INSIGHTS_COLUMN_IDS.FOCUS:
            return null;
        default:
            assertNever(columnId);
    }
};
